import { createFunctionClient } from './api-client.js';
import { enrichPlanetList, normalize360 } from './utils.js';

const BASE_WALL_CLOCK = new Date(Date.UTC(2026, 6, 9, 16, 31, 48));

const NATAL_PLANETS = [
  planet('ascendant', 330 + 24 + 45 / 60 + 29 / 3600, 0, { house: 1 }),
  planet('sun', 270 + 19 + 10 / 60 + 21 / 3600, 0.9856, { house: 11, dignity: 'Enemy' }),
  planet('moon', 180 + 5 + 33 / 60 + 3 / 3600, 13.176, { house: 7, dignity: 'Enemy' }),
  planet('mars', 240 + 14 + 50 / 60 + 39 / 3600, 0.55, { house: 10, dignity: 'Great friend' }),
  planet('mercury', 300 + 1 + 13 / 60 + 49 / 3600, -0.3, { house: 11, dignity: 'Enemy' }),
  planet('jupiter', 300 + 26 + 4 / 60 + 48 / 3600, 0.12, { house: 12, dignity: 'Enemy' }),
  planet('venus', 300 + 10 + 7 / 60 + 53 / 3600, 1.15, { house: 11, dignity: 'Neutral' }),
  planet('saturn', 60 + 19 + 51 / 60 + 4 / 3600, -0.06, { house: 4, dignity: 'Neutral' }),
  planet('rahu', 210 + 14 + 23 / 60 + 38 / 3600, -0.053, { house: 9, dignity: 'Debilitated' }),
  planet('ketu', 30 + 14 + 23 / 60 + 38 / 3600, -0.053, { house: 3, dignity: 'Debilitated' }),
];

const TRANSIT_BASE = [
  planet('ascendant', 210 + 14 + 54 / 60 + 38 / 3600, 360.9856),
  planet('sun', 60 + 23 + 2 / 60 + 18 / 3600, 0.953),
  planet('moon', 14 + 16 / 60 + 27 / 3600, 13.22),
  planet('mars', 30 + 13 + 18 / 60 + 25 / 3600, 0.65),
  planet('mercury', 60 + 28 + 45 / 60 + 3 / 3600, -0.26),
  planet('jupiter', 90 + 7 + 45 / 60 + 39 / 3600, 0.21),
  planet('venus', 120 + 5 + 28 / 60 + 34 / 3600, 1.18),
  planet('saturn', 330 + 20 + 15 / 60 + 49 / 3600, 0.02),
  planet('rahu', 300 + 6 + 28 / 60 + 39 / 3600, -0.053),
  planet('ketu', 120 + 6 + 28 / 60 + 39 / 3600, -0.053),
];

const KAKSHA_SAMPLE = [
  { planetId: 'sun', kakshaIndex: 6, kakshaLord: 'Moon', kakshaBindu: 0, bav: 5, sav: 30 },
  { planetId: 'moon', kakshaIndex: 3, kakshaLord: 'Sun', kakshaBindu: 0, bav: 5, sav: 26 },
  { planetId: 'mars', kakshaIndex: 3, kakshaLord: 'Sun', kakshaBindu: 1, bav: 2, sav: 20 },
  { planetId: 'mercury', kakshaIndex: 7, kakshaLord: 'Ascendant', kakshaBindu: 1, bav: 6, sav: 30 },
  { planetId: 'jupiter', kakshaIndex: 2, kakshaLord: 'Mars', kakshaBindu: 1, bav: 5, sav: 29 },
  { planetId: 'venus', kakshaIndex: 1, kakshaLord: 'Jupiter', kakshaBindu: 0, bav: 4, sav: 31 },
  { planetId: 'saturn', kakshaIndex: 5, kakshaLord: 'Mercury', kakshaBindu: 0, bav: 2, sav: 26 },
];

export function createMockAdvancedAstrologyClient() {
  return createFunctionClient(async (params = {}) => {
    await new Promise((resolve) => setTimeout(resolve, 80));
    const wallClock = parseWallClock(params.localDateTime || '2026-07-09T16:31:48');
    const days = (wallClock.getTime() - BASE_WALL_CLOCK.getTime()) / 86_400_000;
    const transitPlanets = enrichPlanetList(TRANSIT_BASE.map((item) => ({
      ...item,
      longitude: normalize360(item.longitude + item.speed * days),
    })));
    const natalPlanets = enrichPlanetList(NATAL_PLANETS);
    const natalAscendant = natalPlanets.find((item) => item.id === 'ascendant').longitude;
    const transitAscendant = transitPlanets.find((item) => item.id === 'ascendant').longitude;

    return {
      meta: {
        title: params.chartName || 'Avijit Gupta',
        subtitle: 'Inner: Birth Chart · Outer: Gochar',
        localDateTime: params.localDateTime || '2026-07-09T16:31:48',
        displayDateTime: `${(params.localDateTime || '2026-07-09T16:31:48').replace('T', ' ')} ${params.timezone || 'Asia/Kolkata'}`,
        timezone: params.timezone || 'Asia/Kolkata',
        latitude: Number(params.latitude ?? 22.5726),
        longitude: Number(params.longitude ?? 88.3639),
        ayanamsha: 'Lahiri',
        houseSystem: 'Placidus',
        nodeMode: 'true',
        calculationMode: 'mock-demo',
        gocharRuleSetVersion: null,
        kakshaDataSource: 'kundlistar-provider-ashtakavarga',
      },
      natal: {
        ascendant: natalAscendant,
        houses: equalCusps(natalAscendant),
        planets: natalPlanets,
      },
      transit: {
        ascendant: transitAscendant,
        houses: equalCusps(transitAscendant),
        planets: transitPlanets,
      },
      sarvatobhadra: {
        referenceCells: ['Krittika', { row: 4, col: 4 }],
        affectedCells: ['Punarvasu', 'Rohini', 'Magha'],
        vedhas: [
          { from: 'Punarvasu', to: 'Krittika', planet: 'Sun', nature: 'malefic', label: 'Example Sun Vedha line' },
          { from: 'Pushya', to: 'Rohini', planet: 'Jupiter', nature: 'benefic', label: 'Example Jupiter protection line' },
          { from: 'Magha', to: 'Krittika', planet: 'Venus', nature: 'benefic', label: 'Example Venus Vedha line' },
        ],
        trend: 'Calculation view',
      },
      kota: {
        referenceNakshatra: 'Krittika',
        gocharScore: null,
        trend: 'Rule set not configured',
        ruleContributions: [],
      },
      kaksha: KAKSHA_SAMPLE,
    };
  });
}

function planet(id, longitude, speed, extras = {}) {
  return { id, longitude, speed, ...extras };
}

function equalCusps(ascendant) {
  return Array.from({ length: 12 }, (_, index) => normalize360(ascendant + index * 30));
}

function parseWallClock(value) {
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?/);
  if (!match) return new Date(BASE_WALL_CLOCK);
  return new Date(Date.UTC(+match[1], +match[2] - 1, +match[3], +match[4], +match[5], +(match[6] || 0)));
}
